import { ArrowDown, ArrowLeft, ArrowRight, ChevronsDown, Pause, Play, RotateCcw, RotateCw } from "lucide-react";
import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createTetrisState,
  dropTetrisPiece,
  getTetrisDropDelay,
  getTetrisPreviewCells,
  getTetrisVisibleBoard,
  hardDropTetrisPiece,
  moveTetrisPiece,
  rotateTetrisPiece,
  serializeTetrisState,
  toggleTetrisPause,
  type TetrisPieceType,
  type TetrisState,
} from "../data/tetris";

declare global {
  interface Window {
    advance_tetris_time?: (ms: number) => void;
    render_tetris_to_text?: () => string;
  }
}

const TETRIS_COLORS: Record<TetrisPieceType, string> = {
  I: "#00d9ff",
  J: "#3377ff",
  L: "#ff9b2f",
  O: "#ffd52f",
  S: "#20c82b",
  T: "#ff2fd6",
  Z: "#ff4139",
};

export function TetrisPanel() {
  const [state, setState] = useState<TetrisState>(() => ({
    ...createTetrisState("T", "I"),
    status: "paused",
  }));
  const stateRef = useRef(state);
  const visibleBoard = useMemo(() => getTetrisVisibleBoard(state), [state]);
  const previewCells = useMemo(() => getTetrisPreviewCells(state.next), [state.next]);
  const statusLabel = state.status === "lost" ? "Game Over" : state.status;

  const tick = useCallback(() => {
    setState((current) => dropTetrisPiece(current));
  }, []);

  const restart = useCallback(() => {
    setState(createTetrisState());
  }, []);

  const togglePlay = useCallback(() => {
    setState((current) => toggleTetrisPause(current));
  }, []);

  const moveLeft = useCallback(() => {
    setState((current) => moveTetrisPiece(current, -1, 0));
  }, []);

  const moveRight = useCallback(() => {
    setState((current) => moveTetrisPiece(current, 1, 0));
  }, []);

  const softDrop = useCallback(() => {
    setState((current) => dropTetrisPiece(current));
  }, []);

  const hardDrop = useCallback(() => {
    setState((current) => hardDropTetrisPiece(current));
  }, []);

  const rotate = useCallback(() => {
    setState((current) => rotateTetrisPiece(current));
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (state.status !== "playing") {
      return undefined;
    }
    const id = window.setInterval(tick, getTetrisDropDelay(state.level));
    return () => window.clearInterval(id);
  }, [state.level, state.status, tick]);

  useEffect(() => {
    window.render_tetris_to_text = () => JSON.stringify(serializeTetrisState(stateRef.current));
    window.advance_tetris_time = (ms: number) => {
      const steps = Math.max(1, Math.round(ms / getTetrisDropDelay(stateRef.current.level)));
      setState((current) => {
        let next = current;
        for (let index = 0; index < steps; index += 1) {
          next = dropTetrisPiece(next);
        }
        return next;
      });
    };

    return () => {
      delete window.render_tetris_to_text;
      delete window.advance_tetris_time;
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const commands: Partial<Record<string, () => void>> = {
      ArrowLeft: moveLeft,
      ArrowRight: moveRight,
      ArrowDown: softDrop,
      ArrowUp: rotate,
      " ": hardDrop,
      r: restart,
      p: togglePlay,
    };
    const command = commands[event.key];
    if (!command) {
      return;
    }
    event.preventDefault();
    command();
  };

  return (
    <section
      className="legacy-tetris"
      aria-labelledby="legacy-tetris-title"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="legacy-tetris-topline">
        <h2 id="legacy-tetris-title">Tetris</h2>
        <span className="legacy-tetris-status" data-status={state.status}>
          {statusLabel}
        </span>
      </div>

      <div className="legacy-tetris-game">
        <div
          className="legacy-tetris-board"
          role="img"
          aria-label={`Tetris board, ${state.score} score, ${state.lines} lines, active ${state.active.type}`}
        >
          {visibleBoard.flatMap((row, y) =>
            row.map((cell, x) => (
              <span
                className="legacy-tetris-cell"
                data-piece={cell ?? undefined}
                key={`${x}-${y}`}
                style={cell ? ({ "--piece-color": TETRIS_COLORS[cell] } as CSSProperties) : undefined}
              />
            )),
          )}
        </div>

        <div className="legacy-tetris-side">
          <TetrisStat label="Score" value={state.score} />
          <TetrisStat label="Lines" value={state.lines} />
          <TetrisStat label="Level" value={state.level} />
          <div className="legacy-tetris-next" aria-label={`Next piece ${state.next}`}>
            <span>Next</span>
            <div>
              {previewCells.flatMap((row, y) =>
                row.map((filled, x) => (
                  <i
                    key={`${x}-${y}`}
                    data-piece={filled ? state.next : undefined}
                    style={filled ? ({ "--piece-color": TETRIS_COLORS[state.next] } as CSSProperties) : undefined}
                  />
                )),
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="legacy-tetris-controls" role="group" aria-label="Tetris controls">
        <TetrisButton label="Move left" onClick={moveLeft}>
          <ArrowLeft aria-hidden="true" size={17} strokeWidth={2.5} />
        </TetrisButton>
        <TetrisButton label="Rotate" onClick={rotate}>
          <RotateCw aria-hidden="true" size={17} strokeWidth={2.5} />
        </TetrisButton>
        <TetrisButton label="Move right" onClick={moveRight}>
          <ArrowRight aria-hidden="true" size={17} strokeWidth={2.5} />
        </TetrisButton>
        <TetrisButton label="Soft drop" onClick={softDrop}>
          <ArrowDown aria-hidden="true" size={17} strokeWidth={2.5} />
        </TetrisButton>
        <TetrisButton label="Hard drop" onClick={hardDrop}>
          <ChevronsDown aria-hidden="true" size={17} strokeWidth={2.5} />
        </TetrisButton>
        <TetrisButton label={state.status === "playing" ? "Pause Tetris" : "Play Tetris"} onClick={togglePlay}>
          {state.status === "playing" ? (
            <Pause aria-hidden="true" size={17} strokeWidth={2.5} />
          ) : (
            <Play aria-hidden="true" size={17} strokeWidth={2.5} />
          )}
        </TetrisButton>
        <TetrisButton label="Restart Tetris" onClick={restart}>
          <RotateCcw aria-hidden="true" size={17} strokeWidth={2.5} />
        </TetrisButton>
      </div>

      <p className="sr-only" aria-live="polite">
        {statusLabel}. Score {state.score}. Lines {state.lines}. Level {state.level}. Active {state.active.type}. Next{" "}
        {state.next}.
      </p>
    </section>
  );
}

function TetrisStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="legacy-tetris-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function TetrisButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick}>
      {children}
    </button>
  );
}
